/**
 * useChatHistory
 * Gestiona el historial de chat con persistencia por usuario en localStorage.
 * La clave es: chat_history_<username> para aislar conversaciones entre usuarios.
 */
import { useState, useCallback, useEffect } from 'react';

const GREETING = {
    id: 0,
    role: 'assistant',
    content:
        'Hola, soy el Asistente IA del sistema de certificación de Querétaro. Puedes preguntarme sobre expedientes, certificados, dependencias y más. ¿En qué te puedo ayudar?',
    sources: [],
    timestamp: null,
};

/**
 * Obtiene el nombre de usuario actual desde sessionStorage.
 * Retorna 'anonymous' si no hay sesión activa.
 */
function getCurrentUsername() {
    try {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
            const u = JSON.parse(userStr);
            return u?.username || u?.email || 'anonymous';
        }
    } catch {
        // ignorar
    }
    return 'anonymous';
}

function getStorageKey(username) {
    return `chat_history_${username}`;
}

function loadHistory(username) {
    try {
        const raw = localStorage.getItem(getStorageKey(username));
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        }
    } catch {
        // Si hay corrupción, ignorar
    }
    return [{ ...GREETING, timestamp: new Date().toISOString() }];
}

function saveHistory(username, messages) {
    try {
        // Solo guardamos los mensajes sin el flag transitorio `streaming`
    const toSave = messages.map(({ streaming: _streaming, ...rest }) => rest);
        localStorage.setItem(getStorageKey(username), JSON.stringify(toSave));
    } catch {
        // localStorage puede estar lleno; ignorar silenciosamente
    }
}

export function useChatHistory() {
    const [username, setUsername] = useState(() => getCurrentUsername());
    const [messages, setMessages] = useState(() => loadHistory(username));

    // Si cambia el usuario (re-login) recarga el historial correcto
    useEffect(() => {
        const handlePrivileges = () => {
            const newUsername = getCurrentUsername();
            setUsername(newUsername);
            setMessages(loadHistory(newUsername));
        };

        window.addEventListener('privilegesUpdated', handlePrivileges);
        return () => window.removeEventListener('privilegesUpdated', handlePrivileges);
    }, []);

    // Persiste el historial cada vez que cambian los mensajes
    useEffect(() => {
        // No guardar si el streaming aún está activo (mensajes incompletos)
        const hasStreaming = messages.some((m) => m.streaming);
        if (!hasStreaming) {
            saveHistory(username, messages);
        }
    }, [messages, username]);

    const addMessage = useCallback((msg) => {
        setMessages((prev) => [...prev, msg]);
    }, []);

    const updateMessage = useCallback((id, updater) => {
        setMessages((prev) =>
            prev.map((m) => (m.id === id ? { ...m, ...updater(m) } : m))
        );
    }, []);

    const clearHistory = useCallback(() => {
        const fresh = [{ ...GREETING, timestamp: new Date().toISOString() }];
        setMessages(fresh);
        saveHistory(username, fresh);
    }, [username]);

    return { messages, setMessages, addMessage, updateMessage, clearHistory, username };
}
